import type { PublicationReleaseProjection } from "./publication-release-types";

export type PublicationExecutionEnvironment =
  | "preview"
  | "staging"
  | "production";

export interface PublicationExecutionCredentialBinding {
  credential: string;
  idempotencyKey: string;
  adapterId: string;
}

export interface PublicationExecutionAuthorityScope {
  principalId: string;
  projectId: string;
  releaseId: string;
  environment: PublicationExecutionEnvironment;
  authorityRef: string;
  authorityHash: string;
  authorityExpiresAt: string;
}

interface StoredPublicationExecutionCredential extends PublicationExecutionCredentialBinding {
  scope: PublicationExecutionAuthorityScope;
}

const credentialVault = new Map<string, StoredPublicationExecutionCredential>();
const credentialVaultSubscribers = new Set<() => void>();
let credentialVaultRevision = 0;

export function subscribePublicationExecutionCredentials(
  subscriber: () => void,
): () => void {
  credentialVaultSubscribers.add(subscriber);
  return () => credentialVaultSubscribers.delete(subscriber);
}

export function getPublicationExecutionCredentialSnapshot(): number {
  return credentialVaultRevision;
}

/**
 * Resolves the exact signed authority that may own a session-only deployment
 * credential. Missing, stale, consumed, or expired authorities are fail-closed.
 */
export function resolvePublicationExecutionAuthorityScope(
  principalId: string | null | undefined,
  release: PublicationReleaseProjection | null | undefined,
  environment: PublicationExecutionEnvironment,
  now = Date.now(),
): PublicationExecutionAuthorityScope | null {
  if (!principalId || !release) return null;
  const authority =
    environment === "production"
      ? release.authorization?.status === "verified" &&
        release.authorization.environment === "production" &&
        release.authorization.authorizationRef &&
        release.authorization.expiresAt
        ? {
            ref: release.authorization.authorizationRef,
            expiresAt: release.authorization.expiresAt,
          }
        : null
      : release.executionGrant?.status === "verified" &&
          release.executionGrant.environment === environment &&
          release.executionGrant.grantRef &&
          release.executionGrant.expiresAt
        ? {
            ref: release.executionGrant.grantRef,
            expiresAt: release.executionGrant.expiresAt,
          }
        : null;
  if (!authority || !isFutureExpiry(authority.expiresAt, now)) return null;
  return {
    principalId,
    projectId: release.projectId,
    releaseId: release.releaseId,
    environment,
    authorityRef: authority.ref.ref,
    authorityHash: authority.ref.contentHash,
    authorityExpiresAt: authority.expiresAt,
  };
}

/**
 * Stores one opaque execution credential in process memory only. This module
 * intentionally has no serialization, storage, logging, or inspection export.
 */
export function storePublicationExecutionCredential(
  scope: PublicationExecutionAuthorityScope,
  binding: PublicationExecutionCredentialBinding,
): void {
  if (!isFutureExpiry(scope.authorityExpiresAt)) {
    throw new Error("The deployment authority is expired or invalid.");
  }
  credentialVault.set(vaultKey(scope), {
    ...binding,
    scope: { ...scope },
  });
  publishCredentialVaultChange();
}

export function listPublicationExecutionCredentialEnvironments(
  scopes: readonly PublicationExecutionAuthorityScope[],
): PublicationExecutionEnvironment[] {
  return (["preview", "staging", "production"] as const).filter(
    (environment) => {
      const scope = scopes.find((item) => item.environment === environment);
      return Boolean(
        scope &&
        isFutureExpiry(scope.authorityExpiresAt) &&
        credentialVault.has(vaultKey(scope)),
      );
    },
  );
}

/**
 * Reads an exact authority-bound credential without consuming it. It remains
 * available for reconciliation or an idempotent retry until the caller learns
 * a definitive deploy outcome.
 */
export function readPublicationExecutionCredential(
  scope: PublicationExecutionAuthorityScope,
  adapterId: string,
): PublicationExecutionCredentialBinding {
  const key = vaultKey(scope);
  const binding = credentialVault.get(key);
  if (!binding) {
    throw new Error(
      `Request a new ${scope.environment} execution credential before deploying.`,
    );
  }
  if (!isFutureExpiry(scope.authorityExpiresAt)) {
    credentialVault.delete(key);
    publishCredentialVaultChange();
    throw new Error("The deployment authority expired. Request a new one.");
  }
  if (binding.adapterId !== adapterId) {
    throw new Error(
      "The execution credential is bound to a different deployment adapter.",
    );
  }
  return {
    credential: binding.credential,
    idempotencyKey: binding.idempotencyKey,
    adapterId: binding.adapterId,
  };
}

/** Removes only the credential used by the completed/rejected request. */
export function removePublicationExecutionCredential(
  scope: PublicationExecutionAuthorityScope,
  idempotencyKey: string,
): void {
  const key = vaultKey(scope);
  if (credentialVault.get(key)?.idempotencyKey !== idempotencyKey) return;
  credentialVault.delete(key);
  publishCredentialVaultChange();
}

/**
 * Invalidates credentials when the signed authority or authenticated principal
 * changes. This also clears expired entries without exposing their contents.
 */
export function reconcilePublicationExecutionCredentials(input: {
  principalId: string | null | undefined;
  projectId: string;
  releaseId: string;
  validScopes: readonly PublicationExecutionAuthorityScope[];
}): void {
  let changed = false;
  for (const [key, binding] of credentialVault) {
    if (
      binding.scope.projectId !== input.projectId ||
      binding.scope.releaseId !== input.releaseId
    )
      continue;
    const exactScope = input.validScopes.find(
      (scope) => vaultKey(scope) === key && sameAuthority(scope, binding.scope),
    );
    if (
      !input.principalId ||
      binding.scope.principalId !== input.principalId ||
      !exactScope ||
      !isFutureExpiry(binding.scope.authorityExpiresAt)
    ) {
      credentialVault.delete(key);
      changed = true;
    }
  }
  if (changed) publishCredentialVaultChange();
}

export function reconcilePublicationExecutionPrincipal(
  principalId: string | null | undefined,
): void {
  let changed = false;
  for (const [key, binding] of credentialVault) {
    if (
      !principalId ||
      binding.scope.principalId !== principalId ||
      !isFutureExpiry(binding.scope.authorityExpiresAt)
    ) {
      credentialVault.delete(key);
      changed = true;
    }
  }
  if (changed) publishCredentialVaultChange();
}

export function clearPublicationExecutionCredentials(
  projectId?: string,
  releaseId?: string,
): void {
  if (!projectId) {
    if (credentialVault.size === 0) return;
    credentialVault.clear();
    publishCredentialVaultChange();
    return;
  }
  let changed = false;
  for (const [key, binding] of credentialVault) {
    if (
      binding.scope.projectId === projectId &&
      (!releaseId || binding.scope.releaseId === releaseId)
    ) {
      credentialVault.delete(key);
      changed = true;
    }
  }
  if (changed) publishCredentialVaultChange();
}

function publishCredentialVaultChange(): void {
  credentialVaultRevision += 1;
  for (const subscriber of [...credentialVaultSubscribers]) subscriber();
}

function sameAuthority(
  left: PublicationExecutionAuthorityScope,
  right: PublicationExecutionAuthorityScope,
): boolean {
  return (
    left.authorityRef === right.authorityRef &&
    left.authorityHash === right.authorityHash &&
    left.authorityExpiresAt === right.authorityExpiresAt
  );
}

function isFutureExpiry(expiresAt: string, now = Date.now()): boolean {
  const expiresAtMs = Date.parse(expiresAt);
  return Number.isFinite(expiresAtMs) && expiresAtMs > now;
}

function vaultKey(scope: PublicationExecutionAuthorityScope): string {
  return [
    scope.principalId,
    scope.projectId,
    scope.releaseId,
    scope.environment,
    scope.authorityRef,
    scope.authorityHash,
    scope.authorityExpiresAt,
  ].join("\u0000");
}
